import { ApolloError, useApolloClient } from "@apollo/client";
import { CardContainer, CollapseSection, InfoLabelMessage } from "@msys/ui";
import { Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import {
  Form,
  Formik,
  type FormikErrors,
  type FormikHelpers,
  getIn,
  setIn,
} from "formik";
import { isEqual } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import * as Yup from "yup";
import {
  type AutomatedOnboardingTrigger,
  namedOperations,
} from "../../../../clients/graphqlTypes.js";
import { AutoSave } from "../../../commons/form-fields/AutoSave.js";
import {
  CheckboxField,
  CheckboxGroupField,
} from "../../../commons/form-fields/CheckboxField.js";
import { SelectField } from "../../../commons/form-fields/SelectField.js";
import { SwitchField } from "../../../commons/form-fields/SwitchField.js";
import { TextField } from "../../../commons/form-fields/TextField.js";
import { CrmCompanySelect } from "../../crm-companies/CrmCompanySelect.js";
import { TemplateQuoteSelectField } from "../../templates/quote/TemplateQuoteSelectField.js";
import {
  type AutomatedOnboardingTemplateRefFragment,
  type OrganisationAutomatedOnboardingSettingsFragment,
  type OrganisationRefFragment,
  useOrganisationAutomatedOnboardingBox_SetOrganisationAutomatedOnboardingSettingsMutation,
} from "./OrganisationAutomatedOnboardingBox.generated.js";

interface Props {
  settings: OrganisationAutomatedOnboardingSettingsFragment;
}

type ExampleLeadTemplate = Omit<
  AutomatedOnboardingTemplateRefFragment,
  "__typename"
>;

interface ExampleLeadFormValues {
  enabled: boolean;
  // the whole reference, since the template picker takes it as its value
  template: ExampleLeadTemplate | null;
  clientCrmOrganisationId: string | null;
  projectTitle: string;
  quoteTitle: string;
}

interface FormValues {
  enabled: boolean;
  templateOrganisationId: string | null;
  enabledTriggers: AutomatedOnboardingTrigger[];
  exampleLead: ExampleLeadFormValues;
}

// The Formik path of a field a server validation error can show on.
type FormFieldPath =
  | Exclude<keyof FormValues, "enabled" | "exampleLead">
  | `exampleLead.${Exclude<keyof ExampleLeadFormValues, "enabled">}`;

// The display order of the trigger checkboxes. `satisfies` plus the
// `Record<AutomatedOnboardingTrigger, …>` label map below make a new schema
// trigger a compile error until it gets a checkbox.
const TRIGGERS = [
  "CRM_INVITE",
] as const satisfies readonly AutomatedOnboardingTrigger[];

// The input fields the settings API reports validation failures on, each with
// the form field the error shows on. It names the input field as the message
// prefix (`"<field>: <reason>"`) — the ExpectedError's `field` property is not
// transported to the client.
const INVALID_INPUT_FIELDS = [
  { inputField: "templateOrganisationId", formField: "templateOrganisationId" },
  { inputField: "enabledTriggers", formField: "enabledTriggers" },
  { inputField: "exampleLead.templateId", formField: "exampleLead.template" },
  {
    inputField: "exampleLead.clientCrmOrganisationId",
    formField: "exampleLead.clientCrmOrganisationId",
  },
  {
    inputField: "exampleLead.projectTitle",
    formField: "exampleLead.projectTitle",
  },
  { inputField: "exampleLead.quoteTitle", formField: "exampleLead.quoteTitle" },
] as const satisfies readonly {
  inputField: string;
  formField: FormFieldPath;
}[];

type InvalidInputField = (typeof INVALID_INPUT_FIELDS)[number];

// A field the server rejected, with the values that were submitted.
interface ServerFieldError {
  field: InvalidInputField;
  submittedValues: FormValues;
}

function getInvalidInputField(error: unknown): InvalidInputField | null {
  if (!(error instanceof ApolloError)) return null;
  for (const { message } of error.graphQLErrors) {
    const field = INVALID_INPUT_FIELDS.find(candidate =>
      message.startsWith(`${candidate.inputField}: `)
    );
    if (field) return field;
  }
  return null;
}

// Formik's own path helpers, narrowed to the form's paths.
const getFieldValue = (values: FormValues, path: FormFieldPath): unknown =>
  getIn(values, path);
const fieldErrorAt = (
  path: FormFieldPath,
  message: string
): FormikErrors<FormValues> => setIn({}, path, message);

// The template organisation picker's error (the required hint, or a server
// error) shows without the user touching the picker first, like the
// example-lead pickers', which read the errors directly.
const INITIAL_TOUCHED = { templateOrganisationId: true };

function toFormValues(
  settings: OrganisationAutomatedOnboardingSettingsFragment
): FormValues {
  return {
    enabled: settings.enabled,
    templateOrganisationId: settings.templateOrganisation?.id ?? null,
    enabledTriggers: settings.enabledTriggers,
    exampleLead: {
      enabled: settings.exampleLead.enabled,
      template: settings.exampleLead.template
        ? toExampleLeadTemplate(settings.exampleLead.template)
        : null,
      clientCrmOrganisationId:
        settings.exampleLead.clientCrmOrganisation?.id ?? null,
      projectTitle: settings.exampleLead.projectTitle,
      quoteTitle: settings.exampleLead.quoteTitle,
    },
  };
}

function toExampleLeadTemplate({
  id,
  title,
  owningSystemOrganisationId,
}: ExampleLeadTemplate): ExampleLeadTemplate {
  return { id, title, owningSystemOrganisationId };
}

// A ticked example lead whose template or end client is gone (no longer
// available, or deleted) fails validation and so blocks every auto-save; the
// "Advanced" section then opens, so the reason is visible.
function isExampleLeadIncomplete(
  settings: OrganisationAutomatedOnboardingSettingsFragment
): boolean {
  const { enabled, template, clientCrmOrganisation } = settings.exampleLead;
  return enabled && (!template || !clientCrmOrganisation);
}

export function OrganisationAutomatedOnboardingBox({ settings }: Props) {
  const { t } = useTranslate([
    "OrganisationSettings",
    "OrganisationPageTopbar",
    "Global",
  ]);
  const { enqueueSnackbar } = useSnackbar();
  const client = useApolloClient();

  const [setSettings] =
    useOrganisationAutomatedOnboardingBox_SetOrganisationAutomatedOnboardingSettingsMutation(
      {
        client,
        refetchQueries: [
          namedOperations.Query.OrganisationSettingsIntegrations,
        ],
        awaitRefetchQueries: true,
      }
    );

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        enabled: Yup.boolean().required(),
        templateOrganisationId: Yup.string()
          .nullable()
          .when("enabled", {
            is: true,
            then: Yup.string()
              .nullable()
              .required(t("Select a template organisation")),
          }),
        enabledTriggers: Yup.array()
          .of(Yup.string().required())
          .when("enabled", {
            is: true,
            then: Yup.array()
              .of(Yup.string().required())
              .min(1, t("Select at least one trigger.")),
          }),
        exampleLead: Yup.object().shape({
          enabled: Yup.boolean().required(),
          template: Yup.object()
            .nullable()
            .when("enabled", {
              is: true,
              then: Yup.object()
                .nullable()
                .required(t("Select a quote template.")),
            }),
          clientCrmOrganisationId: Yup.string()
            .nullable()
            .when("enabled", {
              is: true,
              then: Yup.string()
                .nullable()
                .required(t("Select an end client.")),
            }),
          projectTitle: Yup.string()
            .trim()
            .required(t("Enter a project name.")),
          quoteTitle: Yup.string().trim().required(t("Enter a quote name.")),
        }),
      }),
    [t]
  );

  const fieldErrorMessages: Record<InvalidInputField["inputField"], string> = {
    templateOrganisationId: t(
      "Select another organisation you are a member of."
    ),
    enabledTriggers: t("Each trigger can only be selected once."),
    "exampleLead.templateId": t("Select a quote template."),
    "exampleLead.clientCrmOrganisationId": t("Select an end client."),
    "exampleLead.projectTitle": t("Enter a project name."),
    "exampleLead.quoteTitle": t("Enter a quote name."),
  };

  // A server error stays on its field until the user changes that field. A
  // plain `setFieldError` would be wiped by the next validation run (e.g. the
  // picker's deferred `setTouched`, or a blur), so `validate` re-derives it;
  // Formik merges that with the schema errors. A ref (not state), so a
  // validation run right after the failed save already sees it.
  const serverFieldErrorRef = React.useRef<ServerFieldError | null>(null);
  const validateServerFieldError = (
    values: FormValues
  ): FormikErrors<FormValues> => {
    const serverFieldError = serverFieldErrorRef.current;
    if (!serverFieldError) return {};
    const { inputField, formField } = serverFieldError.field;
    return isEqual(
      getFieldValue(values, formField),
      getFieldValue(serverFieldError.submittedValues, formField)
    )
      ? fieldErrorAt(formField, fieldErrorMessages[inputField])
      : {};
  };

  const triggerLabels: Record<
    AutomatedOnboardingTrigger,
    { label: string; description: string }
  > = {
    CRM_INVITE: {
      label: t("CRM invite"),
      description: t(
        "Starts when a craftsman accepts an invitation sent to one of your CRM contacts and creates a new organisation during sign-up."
      ),
    },
  };

  // A saved template organisation the user is no longer a member of stays
  // visible, so the picker does not silently show nothing; saving it again is
  // rejected on the field by the server.
  const templateOrganisationOptions: OrganisationRefFragment[] =
    settings.templateOrganisation &&
    !settings.selectableTemplateOrganisations.some(
      organisation => organisation.id === settings.templateOrganisation?.id
    )
      ? [
          ...settings.selectableTemplateOrganisations,
          settings.templateOrganisation,
        ]
      : settings.selectableTemplateOrganisations;

  // Called by `AutoSave` only for a valid form: it skips the save while the
  // schema (template organisation required and at least one trigger while
  // enabled; the example lead's template and end client while it is ticked,
  // and its names always) or a kept server field error fails, and Formik's
  // `submitForm` validates again before calling this.
  const handleSubmit = async (
    values: FormValues,
    helpers: FormikHelpers<FormValues>
  ) => {
    serverFieldErrorRef.current = null;
    try {
      await setSettings({
        variables: {
          input: {
            enabled: values.enabled,
            templateOrganisationId: values.templateOrganisationId,
            enabledTriggers: values.enabledTriggers,
            exampleLead: {
              enabled: values.exampleLead.enabled,
              templateId: values.exampleLead.template?.id ?? null,
              clientCrmOrganisationId:
                values.exampleLead.clientCrmOrganisationId,
              projectTitle: values.exampleLead.projectTitle,
              quoteTitle: values.exampleLead.quoteTitle,
            },
          },
        },
      });
    } catch (error) {
      const field = getInvalidInputField(error);
      if (field) {
        serverFieldErrorRef.current = { field, submittedValues: values };
        helpers.setFieldError(
          field.formField,
          fieldErrorMessages[field.inputField]
        );
        return;
      }
      enqueueSnackbar(t("An error occurred."), { variant: "error" });
    }
  };

  return (
    <CardContainer
      isExpandable
      title={t("Automated Onboarding", { ns: "OrganisationPageTopbar" })}
    >
      <Stack spacing={1} sx={{ p: 1 }}>
        <InfoLabelMessage
          message={t(
            "During onboarding, every organisation created this way receives the template organisation's product catalogues, the Meister Store template subscriptions (including variants) and the PDF template generator."
          )}
        />
        <Formik<FormValues>
          initialValues={toFormValues(settings)}
          initialTouched={INITIAL_TOUCHED}
          validationSchema={validationSchema}
          validate={validateServerFieldError}
          validateOnMount
          onSubmit={handleSubmit}
        >
          {({ values, errors, setFieldValue }) => (
            <Form>
              <Stack spacing={2}>
                <SwitchField
                  name="enabled"
                  label={t("Enable automated onboarding")}
                  disabled={false}
                />

                <Stack spacing={0.5}>
                  <SelectField<string>
                    name="templateOrganisationId"
                    label={t("Template organisation")}
                    fullWidth
                    disabled={false}
                    // Clearing is the way out of a saved organisation the user
                    // is no longer a member of: the server rejects any save that
                    // still carries it, even while disabled.
                    clearable
                    options={templateOrganisationOptions.map(organisation => ({
                      key: organisation.id,
                      value: organisation.id,
                      label: organisation.name,
                    }))}
                  />
                  {templateOrganisationOptions.length === 0 ? (
                    <Typography variant="caption" color="textSecondary">
                      {t(
                        "No other organisation available. The template organisation must be an organisation you are also a member of."
                      )}
                    </Typography>
                  ) : null}
                </Stack>

                <CheckboxGroupField<AutomatedOnboardingTrigger>
                  name="enabledTriggers"
                  label={t("Triggers")}
                  disabled={false}
                  options={TRIGGERS.map(trigger => ({
                    value: trigger,
                    label: (
                      <Stack component="span">
                        <span>{triggerLabels[trigger].label}</span>
                        <Typography
                          component="span"
                          variant="caption"
                          color="textSecondary"
                        >
                          {triggerLabels[trigger].description}
                        </Typography>
                      </Stack>
                    ),
                  }))}
                />

                <CollapseSection
                  title={t("Advanced")}
                  // `CollapseSection` is expanded unless told otherwise
                  isInitiallyExpanded={isExampleLeadIncomplete(settings)}
                >
                  <Stack spacing={2} sx={{ pt: 1 }}>
                    <CheckboxField
                      name="exampleLead.enabled"
                      label={
                        <Stack component="span">
                          <span>{t("Send an example lead")}</span>
                          <Typography
                            component="span"
                            variant="caption"
                            color="textSecondary"
                          >
                            {t(
                              "Every new partner organisation receives a material quote created from this template as its first lead, addressed to it and made out for the selected end client."
                            )}
                          </Typography>
                        </Stack>
                      }
                    />
                    <TemplateQuoteSelectField
                      inputLabel={t("Quote template")}
                      filterDocumentType="QUOTE"
                      value={values.exampleLead.template}
                      onChange={async template => {
                        await setFieldValue(
                          "exampleLead.template",
                          template ? toExampleLeadTemplate(template) : null
                        );
                      }}
                      required={values.exampleLead.enabled}
                      error={errors.exampleLead?.template}
                    />
                    <CrmCompanySelect
                      inputLabel={t("End client")}
                      crmCompanyId={values.exampleLead.clientCrmOrganisationId}
                      onChange={crmCompany =>
                        setFieldValue(
                          "exampleLead.clientCrmOrganisationId",
                          crmCompany?.id ?? null
                        )
                      }
                      required={values.exampleLead.enabled}
                      canCreateNew
                      createNewLabel={t("Create new contact", { ns: "Global" })}
                      error={errors.exampleLead?.clientCrmOrganisationId}
                    />
                    <TextField
                      name="exampleLead.projectTitle"
                      label={t("Project name")}
                      required
                    />
                    <TextField
                      name="exampleLead.quoteTitle"
                      label={t("Quote name")}
                      required
                    />
                  </Stack>
                </CollapseSection>

                <AutoSave />
              </Stack>
            </Form>
          )}
        </Formik>
      </Stack>
    </CardContainer>
  );
}
